import "./share-avatar.scss";
import { useRef, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { db } from "@/Utils/firebase";
import {
  updateDoc,
  doc,
  arrayUnion,
  getDocs,
  where,
  query,
  collection,
} from "firebase/firestore";
import { useQueryClient, useMutation } from "@tanstack/react-query";

import { ReactComponent as CloseIcon } from "@/Icons/x.svg";
import { ReactComponent as ShareIcon } from "@/Icons/send.svg";
import { ReactComponent as CopyIcon } from "@/Icons/copy.svg";
import { ReactComponent as ExportIcon } from "@/Icons/export.svg";
import { ReactComponent as LockedIcon } from "@/Icons/locked.svg";
import { ReactComponent as GlobeIcon } from "@/Icons/globe.svg";
import { ReactComponent as ArrowDownIcon } from "@/Icons/arrow-down.svg";
import { ReactComponent as CheckedIcon } from "@/Icons/checked.svg";
import { MultiInput } from "@/Components/Multi-Input";
import { Spinner } from "@/Components/Spinner";
import { useShareHistory } from "@/hooks/useShareHistory";
import { useScanData } from "@/hooks/useScanData";

type ShareAvatarProps = {
  isOpen: boolean;
  onClose: () => void;
};

const SelectButton = ({
  onClick,
  isPublic,
}: {
  onClick: () => void;
  isPublic: boolean;
}) => {
  return (
    <button className="select-button p1" onClick={onClick}>
      {!isPublic ? <LockedIcon /> : <GlobeIcon />}
      {isPublic ? "Anyone with this link" : "Only you can view"}
      <ArrowDownIcon />
    </button>
  );
};

const SelectMenu = ({
  isPublic,
  onSelect,
  scanId,
}: {
  isPublic: boolean;
  onSelect: (value: boolean) => void;
  scanId: string;
}) => {
  const queryClient = useQueryClient();
  const updateScanShare = (value: boolean) => {
    // update the isPublic value in the db
    const docRef = doc(db, "scans", scanId);
    return updateDoc(docRef, {
      isPublic: value,
    });
  };
  const selectMutation = useMutation({
    mutationFn: updateScanShare,
    onMutate: async (value: boolean) => {
      await queryClient.cancelQueries({
        queryKey: ["scanData", scanId],
      });
      const previousValue =
        (queryClient.getQueryData(["scanData", scanId]) as any) || {};
      queryClient.setQueryData(["scanData", scanId], {
        ...previousValue,
        isPublic: value,
      });
      return { previousValue };
    },
    onError: (err, value, context) => {
      queryClient.setQueryData(["scanData", scanId], context?.previousValue);
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["scanData", scanId],
      });
    },
  });
  return (
    <div className="select-menu">
      <button
        className={isPublic ? "" : "active"}
        onClick={() => {
          selectMutation.mutate(false);
          onSelect(false);
        }}
      >
        <span className="flex-container">
          <LockedIcon />
          <span className="text-container">
            <span className="h2">Only you can view</span>
            <span className="p3">
              Only you can view your avatar measurements using this link.
            </span>
          </span>
          {!isPublic && <CheckedIcon className="checked-icon" />}
        </span>
      </button>
      <button
        className={isPublic ? "active" : ""}
        onClick={() => {
          selectMutation.mutate(true);
          onSelect(true);
        }}
      >
        <span className="flex-container">
          <GlobeIcon />
          <span className="text-container">
            <span className="h2">Anyone with this link</span>
            <span className="p3">
              Anyone can view your avatar measurements using this link.
            </span>
          </span>
          {isPublic && <CheckedIcon className="checked-icon" />}
        </span>
      </button>
    </div>
  );
};

export const ShareAvatar = ({ isOpen, onClose }: ShareAvatarProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [clientId, setClientId] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [showToast, setShowToast] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const location = useLocation();
  const { date, id } = location.state || {};
  const { data: shareClients } = useShareHistory(id);
  const { data: scanData } = useScanData(id);
  const queryClient = useQueryClient();
  const clientIds = scanData?.clientIds || [];

  useEffect(() => {
    if (error) {
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  useEffect(() => {
    if (showToast) {
      const timeout = setTimeout(() => {
        setShowToast(false);
        clearTimeout(timeout);
      }, 6000);
    }
  }, [showToast]);

  const handleClose = () => {
    // add class to animate out
    containerRef.current?.classList.add("close");
    setTimeout(() => {
      onClose();
    }, 300); // wait for animation to finish
  };

  const handleClientIdChange = (value: string) => {
    setClientId(value);
  };

  const handleSend = async () => {
    if (!clientId || clientId.length !== 6) {
      setError("Invalid share code");
      return;
    }
    try {
      if (clientIds.includes(clientId)) {
        return setError("Avatar already shared with this share code");
      }
      setLoading(true);
      // check if the share code is valid(exists in the db)
      const q = query(
        collection(db, "clientIds"),
        where("clientId", "==", clientId)
      );
      const snapshot = await getDocs(q);
      if (snapshot.empty) {
        return setError("Invalid share code");
      }
      const docRef = doc(db, "scans", id);
      await updateDoc(docRef, {
        clientIds: arrayUnion(clientId),
      });
      // revalidate the share history
      await queryClient.invalidateQueries({
        queryKey: ["scanData", id],
      });
      setLoading(false);
    } catch (e) {
      console.error(e);
      setError("Failed to share avatar");
    } finally {
      setLoading(false);
    }
  };

  const handleShareLink = () => {
    // share link using navigator.share
    if (navigator.share) {
      const domain = process.env.REACT_APP_DASHBOARD_DOMAIN;
      const url = `${domain}/avatars/viewer/${id}`;
      navigator
        .share({
          title: "TrueToForm Avatar",
          text: "Check out this avatar",
          url,
        })
        .catch((error) => {
          console.error("Error sharing", error);
        });
    }
  };

  const handleCopyLink = () => {
    const domain = process.env.REACT_APP_DASHBOARD_DOMAIN;
    const url = `${domain}/avatars/viewer/${id}`;
    navigator.clipboard.writeText(url);
    setShowToast(true);
  };

  const handleMenuClick = () => {
    setMenuOpen(!menuOpen);
  };

  if (!isOpen || !id) return null;

  return (
    <div className="bottom-sheet open" ref={containerRef}>
      <div className="share-avatar-header">
        <h1 className="h1">Share Avatar</h1>
        <p className="p1">{date}</p>
        <button onClick={handleClose}>
          <CloseIcon />
        </button>
      </div>
      <div className="main">
        <div className="share-code">
          <h2 className="h2">Share with a code</h2>
          <MultiInput
            onChange={handleClientIdChange}
            error={error}
            inputClassName="share-code-input h1"
            containerClassName="input-container"
          />
          <p className="error-message p1">{error}</p>
          <div className="button-container">
            <p className="p1 desc">
              Send this avatar to another TrueToForm user using their 6-digit
              share code
            </p>
            {loading ? (
              <Spinner />
            ) : (
              <button className="secondary-button" onClick={handleSend}>
                Send
              </button>
            )}
          </div>
        </div>
        <div className="share-with-link">
          <h2 className="h2">Share with a link</h2>
          <div className="share-container">
            <SelectButton
              onClick={handleMenuClick}
              isPublic={Boolean(scanData?.isPublic)}
            />
            <div className="divider"></div>
            <CopyIcon onClick={handleCopyLink} />
            <ExportIcon onClick={handleShareLink} />
          </div>
          {menuOpen && (
            <SelectMenu
              isPublic={Boolean(scanData?.isPublic)}
              onSelect={() => {
                setMenuOpen(false);
              }}
              scanId={id}
            />
          )}
        </div>
        <div className="share-history">
          <h4 className="h4 share-history-title">Share history</h4>
          {shareClients?.length === 0 ? (
            <h3 className="h3 no-history">No share history</h3>
          ) : (
            <div className="share-history-container">
              {shareClients?.map((client, index) => (
                <div className="share-history-item" key={index}>
                  <ShareIcon />
                  <div>
                    <p className="h3">Sent to {client.orgName}</p>
                    <p className="p1">Share code: {client.clientId}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {showToast && (
        <div className="toast">
          <p className="p1">Copied URL to clipboard</p>
        </div>
      )}
    </div>
  );
};
